// <s-order-tracker>: looks up a Delhivery shipment through the store's tracking proxy (same endpoint as the live theme)
// and shows a status banner, 4-step progress, shipment details and the scan history.
// Translated copy arrives as data-* attributes; ?waybill= or ?order_id= in the URL starts a lookup automatically.
const STEP_BY_STATUS = {
  Manifested: 1,
  'Not Picked': 1,
  Pending: 2,
  'In Transit': 2,
  Dispatched: 3,
  'Out for Delivery': 3,
  Delivered: 4,
};

class OrderTracker extends HTMLElement {
  connectedCallback() {
    this.api = this.dataset.api;
    this.form = this.querySelector('[data-track-form]');
    this.input = this.querySelector('[data-track-input]');
    this.views = {
      search: this.querySelector('[data-view="search"]'),
      loading: this.querySelector('[data-view="loading"]'),
      error: this.querySelector('[data-view="error"]'),
      result: this.querySelector('[data-view="result"]'),
    };
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const id = this.input.value.trim();
      if (!id) return this.input.focus();
      this.lookup(id);
    });
    this.querySelectorAll('[data-track-reset]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.show('search');
        this.input.focus();
      })
    );
    const params = new URLSearchParams(window.location.search);
    const preset = params.get('waybill') || params.get('order_id');
    if (preset) {
      this.input.value = preset;
      this.lookup(preset);
    }
  }

  show(name) {
    Object.entries(this.views).forEach(([key, el]) => el?.toggleAttribute('hidden', key !== name));
    if (name === 'error' || name === 'result') this.views[name]?.focus({ preventScroll: false });
  }

  async lookup(id) {
    this.show('loading');
    try {
      const res = await fetch(`${this.api}/${encodeURIComponent(id)}`);
      const json = await res.json();
      if (json?.success && json.data) {
        this.render(json.data);
        this.show('result');
      } else {
        this.show('error');
      }
    } catch {
      this.show('error');
    }
  }

  formatDate(value) {
    if (!value) return this.dataset.na;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return this.dataset.na;
    return date.toLocaleString(document.documentElement.lang || 'en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  render(data) {
    const step = STEP_BY_STATUS[data.status] || 2;
    const copy = {
      1: [this.dataset.confirmedHeading, this.dataset.confirmedSub],
      2: [this.dataset.transitHeading, this.dataset.transitSub],
      3: [this.dataset.outHeading, this.dataset.outSub],
      4: [this.dataset.deliveredHeading, this.dataset.deliveredSub],
    }[step];
    this.querySelector('[data-status-heading]').textContent = copy[0];
    this.querySelector('[data-status-sub]').textContent = copy[1];

    this.querySelector('[data-progress]').style.inlineSize = `${((step - 1) / 3) * 100}%`;
    this.querySelectorAll('[data-step]').forEach((el, i) => el.toggleAttribute('data-active', i < step));

    this.querySelector('[data-tracking-id]').textContent = data.trackingId || this.dataset.na;
    this.querySelector('[data-last-updated]').textContent = this.formatDate(data.statusDate);
    this.querySelector('[data-origin]').textContent = data.origin || this.dataset.na;

    const dest = this.querySelector('[data-destination]');
    dest.replaceChildren();
    const c = data.consignee;
    const parts = c
      ? [Array.isArray(c.Address1) ? c.Address1.join(', ') : c.Address1, c.City, c.State, c.PinCode, c.Country].filter(Boolean)
      : [];
    if (parts.length === 0) dest.textContent = this.dataset.na;
    parts.forEach((line, i) => {
      if (i) dest.append(document.createElement('br'));
      dest.append(document.createTextNode(String(line)));
    });

    const list = this.querySelector('[data-timeline]');
    const template = this.querySelector('[data-timeline-item]');
    list.replaceChildren();
    const scans = (data.scans || []).map((s) => s?.ScanDetail).filter(Boolean);
    if (scans.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'text-sm text-on-surface-secondary';
      empty.textContent = this.dataset.noHistory;
      list.append(empty);
      return;
    }
    scans.forEach((scan) => {
      const item = template.content.firstElementChild.cloneNode(true);
      item.querySelector('[data-scan-status]').textContent = scan.Scan || '';
      const loc = item.querySelector('[data-scan-location]');
      if (scan.ScannedLocation) loc.textContent = scan.ScannedLocation;
      else loc.remove();
      item.querySelector('[data-scan-date]').textContent = scan.ScanDateTime ? this.formatDate(scan.ScanDateTime) : '';
      const note = item.querySelector('[data-scan-note]');
      if (scan.Instructions) note.textContent = scan.Instructions;
      else note.remove();
      list.append(item);
    });
  }
}

if (!customElements.get('s-order-tracker')) customElements.define('s-order-tracker', OrderTracker);
